/**
 * One-shot SFX player with voice stealing (max concurrent sources).
 */

import type { AudioBusGraph } from "./buses";
import type { AudioChildBusId } from "./settings";

const MAX_VOICES = 8;

interface ActiveVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  startedAt: number;
}

export type PlayOneShotOpts = {
  bus: AudioChildBusId;
  gain?: number;
  playbackRate?: number;
};

export class SfxPlayer {
  private readonly context: AudioContext;
  private readonly graph: AudioBusGraph;
  private readonly voices: ActiveVoice[] = [];
  private disposed = false;

  constructor(context: AudioContext, graph: AudioBusGraph) {
    this.context = context;
    this.graph = graph;
  }

  /**
   * Play a buffer once into the given bus. Steals oldest voice if at cap.
   */
  play(buffer: AudioBuffer, opts: PlayOneShotOpts): void {
    if (this.disposed) return;
    if (buffer.length === 0) return;

    while (this.voices.length >= MAX_VOICES) {
      this.stopVoice(this.voices[0]!);
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = opts.playbackRate ?? 1;

    const gain = this.context.createGain();
    const g = Math.max(0, Math.min(2, opts.gain ?? 1));
    gain.gain.value = g;

    source.connect(gain);
    gain.connect(this.graph.getBus(opts.bus));

    const voice: ActiveVoice = {
      source,
      gain,
      startedAt: this.context.currentTime,
    };
    this.voices.push(voice);

    source.onended = () => {
      this.removeVoice(voice);
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        /* already disconnected */
      }
    };

    try {
      source.start();
    } catch {
      this.removeVoice(voice);
    }
  }

  stopAll(): void {
    const copy = [...this.voices];
    for (const v of copy) this.stopVoice(v);
  }

  dispose(): void {
    this.disposed = true;
    this.stopAll();
  }

  activeCount(): number {
    return this.voices.length;
  }

  private stopVoice(voice: ActiveVoice): void {
    try {
      voice.source.onended = null;
      voice.source.stop();
    } catch {
      /* already stopped */
    }
    try {
      voice.source.disconnect();
      voice.gain.disconnect();
    } catch {
      /* */
    }
    this.removeVoice(voice);
  }

  private removeVoice(voice: ActiveVoice): void {
    const i = this.voices.indexOf(voice);
    if (i >= 0) this.voices.splice(i, 1);
  }
}
